import { useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  acknowledgeChangelogRelease,
  listChangelogReleases,
  type ChangelogReleaseListResponse,
} from '@/api'
import useLang from '@/hooks/use-lang'
import { useAuth } from '@/hooks/use-auth'
import type { Release } from '../types'
import { mapApiReleaseToUi } from '../lib/map-api-release'
import { normalizeChangelogListResponse } from '../lib/normalize-changelog-response'

const changelogLocale = (lang: string): 'fr' | 'en' | 'de' =>
  lang === 'fr' || lang === 'de' ? lang : 'en'

const CHANGELOG_QUERY_KEY = 'changelog-releases'

export type ChangelogReleasesState = {
  releases: Release[]
  isLoading: boolean
  isError: boolean
  isEmpty: boolean
  /** Afficher l’entrée sidenav « Nouveautés » (au moins une release publiée). */
  hasReleases: boolean
  showNewBadge: boolean
  latestReleaseId?: string
  /** À appeler uniquement depuis le bouton « Compris » — POST /acknowledge puis refresh cache. */
  acknowledgeOnUnderstood: (release: Release) => Promise<void>
}

const patchMetaAfterAcknowledge = (
  current: ChangelogReleaseListResponse,
  acknowledgedReleaseId: string,
  /** Bouton « Compris » : masquer la pastille sidenav dès l’accusé de lecture. */
  clearNavBadge = false
): ChangelogReleaseListResponse => {
  const latestId = current.meta?.latestReleaseId
  const clearsBadge =
    clearNavBadge ||
    latestId == null ||
    latestId === acknowledgedReleaseId
  return {
    ...current,
    meta: {
      ...current.meta,
      hasUnreadLatest: clearsBadge ? false : (current.meta?.hasUnreadLatest ?? false),
      latestReleaseId: latestId ?? undefined,
    },
  }
}

export const useChangelogReleases = (): ChangelogReleasesState => {
  const { lang } = useLang()
  const { isAuthenticated, isImpersonating } = useAuth()
  const queryClient = useQueryClient()
  const locale = changelogLocale(lang)
  const queryKey = [CHANGELOG_QUERY_KEY, lang] as const

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await listChangelogReleases({ locale })
      return normalizeChangelogListResponse(response)
    },
    enabled: isAuthenticated,
    retry: 2,
    onError: (err) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[changelog] listChangelogReleases failed', err)
      }
    },
  })

  const releases: Release[] = useMemo(
    () => (data?.data ?? []).map((item) => mapApiReleaseToUi(item, locale)),
    [data, locale]
  )

  const hasReleases = !isLoading && !isError && releases.length > 0

  // En « en tant que », les nouveautés ne s'imposent pas (la pastille ouvre la fenêtre d'elle-même), et « Compris »
  // ne les marque pas lues au nom du membre (recette du 29/10/2026).
  const showNewBadge =
    hasReleases && data?.meta?.hasUnreadLatest === true && !isImpersonating

  const acknowledgeMutation = useMutation({
    mutationFn: (releaseId: string) => acknowledgeChangelogRelease(releaseId),
    onMutate: async (releaseId) => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<ChangelogReleaseListResponse>(queryKey)
      if (previous) {
        queryClient.setQueryData<ChangelogReleaseListResponse>(
          queryKey,
          patchMetaAfterAcknowledge(previous, releaseId, true)
        )
      }
      return { previous }
    },
    onSuccess: (_data, releaseId) => {
      queryClient.setQueryData<ChangelogReleaseListResponse>(queryKey, (current) =>
        current ? patchMetaAfterAcknowledge(current, releaseId, true) : current
      )
    },
    onError: (_err, _releaseId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      }
    },
    onSettled: (_data, error) => {
      if (error) {
        void queryClient.invalidateQueries({ queryKey })
      }
    },
  })

  const acknowledgeOnUnderstood = useCallback(
    async (release: Release) => {
      if (!release.releaseId || isImpersonating) return
      const latestId = data?.meta?.latestReleaseId
      const isLatestSlide = releases[0]?.releaseId === release.releaseId
      const releaseIdToAck =
        isLatestSlide && latestId ? latestId : release.releaseId
      await acknowledgeMutation.mutateAsync(releaseIdToAck)
    },
    [
      acknowledgeMutation,
      data?.meta?.latestReleaseId,
      releases,
      isImpersonating,
    ]
  )

  return {
    releases,
    isLoading,
    isError,
    isEmpty: !isLoading && !isError && releases.length === 0,
    hasReleases,
    showNewBadge,
    latestReleaseId: data?.meta?.latestReleaseId ?? undefined,
    acknowledgeOnUnderstood,
  }
}
